import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';

export function LandingPage() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { t } = useLanguage();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubmitted(true);
      setTimeout(() => {
        window.location.href = `/register?email=${encodeURIComponent(email)}`;
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 via-white to-primary-50">
      {/* Navigation */}
      <nav className="px-6 py-4 bg-white/80 backdrop-blur-md border-b border-primary-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto relative flex items-center">
          <div className="flex items-center justify-start sm:hidden">
            <LanguageSwitcher />
          </div>

          <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
            <img
              src="/NestQ_logo_2026.png"
              alt="NestQ"
              className="h-12 rounded-lg"
            />
          </div>

          <div className="ml-auto flex items-center justify-end gap-3 sm:gap-6">
            <div className="hidden sm:flex">
              <LanguageSwitcher />
            </div>
            <button
              onClick={() => window.location.href = '/login'}
              className="px-4 py-2 text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
            >
              {t.landingPage.signIn}
            </button>
            <button
              onClick={() => window.location.href = '/register'}
              className="hidden sm:inline-flex px-6 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all transform hover:scale-105"
            >
              {t.landingPage.getStarted}
            </button>
          </div>
        </div>
      </nav>
      {/* Hero Section with NestQ Character */}

      <section className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
        <div className="relative h-[320px] sm:h-[420px] md:h-[640px] lg:h-[650px]">
          <img
            src="https://sbuehpoxvuufciwzoqaq.supabase.co/storage/v1/object/public/app-images/NestQ_happy_in_forest.png"
            alt="NestQ"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-transparent to-white/70" />
        </div>
      </section>

      {/* Hero Section with NestQ Character */}
      <section className="px-6 py-20 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          {/* Story Content */}
          <div className="max-w-4xl mx-auto space-y-6 text-left">
            <h1 className="text-4xl md:text-5xl font-bold text-primary-700 leading-tight text-center mb-8">
              {t.landingPage.title}
              <span className="block text-primary-700 mt-2">
                {t.landingPage.subtitle}
              </span>
            </h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed font-medium mb-6">
                {t.landingPage.story1}
              </p>

              <p className="text-lg text-gray-700 leading-relaxed font-medium mb-6">
                {t.landingPage.story2}
              </p>

              <p className="text-lg text-gray-700 leading-relaxed font-medium mb-6">
                {t.landingPage.story3}
              </p>

              <p className="text-lg text-gray-700 leading-relaxed font-medium mb-6">
                {t.landingPage.story4}
              </p>

              <p className="text-lg text-gray-700 leading-relaxed font-medium mb-6">
                {t.landingPage.story5}
              </p>

              <p className="text-2xl font-bold text-primary-700 text-center mt-8">
                {t.landingPage.welcome}
              </p>
            </div>
          </div>
          
          {/* CTA */}
          <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 w-full max-w-2xl">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.landingPage.emailPlaceholder}
                className="flex-1 px-4 py-3 border border-primary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
              <button
                type="submit"
                className="px-12 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all transform hover:scale-105 font-medium flex items-center justify-center whitespace-nowrap min-w-fit"
              >
                {isSubmitted ? t.landingPage.redirecting : t.landingPage.beginJourney}
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </form>
          </div>
          <p className="text-sm text-gray-500 mt-4">{t.landingPage.noCreditCard}</p>
        </div>
      </section>
    </div>
  );
}
